import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { RefCallback } from "react";

/**
 * Detects whether an element's content is horizontally truncated (i.e. its
 * CSS `truncate`/`text-overflow: ellipsis` is actually clipping text).
 *
 * Returns a callback ref to attach to the truncating element and the current
 * truncation state. Re-measures whenever the element resizes (e.g. a grid
 * column is resized) and after every commit — the element is typically a
 * block box whose width is independent of its content, so a content change
 * alone never fires the ResizeObserver.
 */
export function useIsTruncated<T extends HTMLElement>(): [
  RefCallback<T>,
  boolean,
] {
  const [isTruncated, setIsTruncated] = useState(false);
  const elementRef = useRef<T | null>(null);

  const measure = useCallback(() => {
    const element = elementRef.current;
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, []);

  // No dependency array: re-measuring after every commit is what keeps the
  // state fresh when only the rendered text changes. The state update bails
  // out when the value is unchanged, so this cannot loop.
  useLayoutEffect(measure);

  const ref: RefCallback<T> = useCallback(
    (element: T | null) => {
      elementRef.current = element;
      if (!element) {
        return;
      }

      const observer = new ResizeObserver(measure);
      observer.observe(element);

      return () => observer.disconnect();
    },
    [measure],
  );

  return [ref, isTruncated];
}
